import { GA_MEASUREMENT_ID } from "../gaMeasurementId";

/**
 * Send GA4 events via gtag (loaded in public/index.html).
 * This avoids react-ga4's init gate and matches the configured property.
 */
function sendGa4Event(eventName, params = {}) {
  if (typeof window === "undefined") return;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => {
      if (v === undefined || v === null) return false;
      if (typeof v === "string" && v.trim() === "") return false;
      return true;
    })
  );
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, cleaned);
    }
  } catch (_) {
    /* ignore */
  }
}

export function setHostAnalyticsUser(hostId) {
  if (!hostId || typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("config", GA_MEASUREMENT_ID, {
        user_id: String(hostId),
      });
    }
  } catch (_) {
    /* ignore */
  }
}

export function trackHostLoginSuccess({ hostId, method }) {
  setHostAnalyticsUser(hostId);
  /* GA4 recommended event — shows up clearly under the name "login" in reports. */
  sendGa4Event("login", {
    method: method || "unknown",
    ...(hostId ? { host_id: String(hostId) } : {}),
  });
}

export function trackConciergeServiceToggle({
  hostId,
  serviceId,
  enabled,
  enabledConciergeCount,
  copyVariant,
}) {
  sendGa4Event("concierge_promo_service_toggle", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    service_id: String(serviceId),
    enabled: enabled ? 1 : 0,
    enabled_concierge_count: Number(enabledConciergeCount) || 0,
    ...(copyVariant ? { copy_variant: String(copyVariant) } : {}),
  });
}

export function trackConciergePromoDismiss({
  hostId,
  dismissCount,
  copyVariant,
}) {
  sendGa4Event("concierge_promo_dismiss", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    dismiss_count: Number(dismissCount) || 0,
    ...(copyVariant ? { copy_variant: String(copyVariant) } : {}),
  });
}

export function trackConciergeSubscribeClick({
  hostId,
  source,
  tier,
  placement,
  enabledConciergeCount,
  priceShownUsd,
  copyVariant,
}) {
  sendGa4Event("concierge_subscribe_click", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    ...(source ? { source: String(source) } : {}),
    tier: tier || "basic",
    ...(placement ? { placement: String(placement) } : {}),
    ...(enabledConciergeCount != null
      ? { enabled_concierge_count: Number(enabledConciergeCount) }
      : {}),
    ...(priceShownUsd != null
      ? { price_shown_usd: Number(priceShownUsd) }
      : {}),
    ...(copyVariant ? { copy_variant: String(copyVariant) } : {}),
  });
}

export function trackConciergeSubscribeError({ hostId, source, message }) {
  sendGa4Event("concierge_subscribe_error", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    ...(source ? { source: String(source) } : {}),
    error_message: String(message || "unknown").slice(0, 120),
  });
}

export function trackConciergeLearnMore({ hostId, source, copyVariant }) {
  sendGa4Event("concierge_learn_more_click", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    ...(source ? { source: String(source) } : {}),
    ...(copyVariant ? { copy_variant: String(copyVariant) } : {}),
  });
}

export function trackConciergeSubscriptionPageView({
  hostId,
  subscriptionStatus,
}) {
  sendGa4Event("concierge_subscription_page_view", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    ...(subscriptionStatus
      ? { subscription_status: String(subscriptionStatus) }
      : {}),
  });
}

export function trackConciergeCheckoutReturn({ hostId, statusParam }) {
  sendGa4Event("concierge_checkout_return", {
    ...(hostId ? { host_id: String(hostId) } : {}),
    ...(statusParam ? { query_status: String(statusParam) } : {}),
  });
}

export function trackConciergeManageBillingClick({ hostId }) {
  sendGa4Event("concierge_manage_billing_click", {
    ...(hostId ? { host_id: String(hostId) } : {}),
  });
}

export function trackConciergeCancelConfirmed({ hostId }) {
  sendGa4Event("concierge_cancel_confirmed", {
    ...(hostId ? { host_id: String(hostId) } : {}),
  });
}
